
import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calendar, List, Grid3X3 } from 'lucide-react';
import { CalendarEvent } from '@/types/calendar';
import { 
  CalendarContentProps, 
  DateClickInfo, 
  EventClickInfo, 
  EventChangeInfo 
} from '../types/calendar';

export const CalendarContent = ({
  calendarRef,
  viewMode,
  formattedEvents,
  handleDateClick,
  handleEventClick,
  handleEventChange,
  setViewMode
}: CalendarContentProps) => {
  const handleNext = () => {
    const calendarElement = calendarRef.current;
    if (calendarElement) {
      const calendarApi = (calendarElement as any)?.calendar;
      calendarApi?.next();
    }
  };

  const handlePrev = () => {
    const calendarElement = calendarRef.current;
    if (calendarElement) {
      const calendarApi = (calendarElement as any)?.calendar;
      calendarApi?.prev();
    }
  };

  const handleToday = () => {
    const calendarElement = calendarRef.current;
    if (calendarElement) {
      const calendarApi = (calendarElement as any)?.calendar;
      calendarApi?.today();
    }
  };

  // Type guard to validate CalendarEvent structure - fixed predicate
  const isValidCalendarEvent = (obj: unknown): obj is CalendarEvent => {
    if (!obj || typeof obj !== 'object') return false;
    const event = obj as Record<string, unknown>;
    return typeof event.id === 'string' &&
           typeof event.title === 'string' &&
           typeof event.start_time === 'string' &&
           typeof event.end_time === 'string';
  };

  // FullCalendar event handlers with proper typing
  const onEventClick = (arg: { event: { id: string; title: string; extendedProps: Record<string, unknown> } }) => {
    if (isValidCalendarEvent(arg.event.extendedProps)) {
      const eventInfo: EventClickInfo = {
        event: {
          id: arg.event.id,
          title: arg.event.title,
          extendedProps: arg.event.extendedProps
        }
      };
      handleEventClick(eventInfo);
    }
  };

  const onEventChange = (arg: { 
    event: { 
      id: string; 
      title: string; 
      start: Date; 
      end: Date; 
      extendedProps: Record<string, unknown> 
    }; 
    revert: () => void 
  }) => {
    if (isValidCalendarEvent(arg.event.extendedProps)) {
      const eventInfo: EventChangeInfo = {
        event: {
          id: arg.event.id,
          title: arg.event.title,
          start: arg.event.start,
          end: arg.event.end,
          extendedProps: arg.event.extendedProps
        },
        revert: arg.revert
      };
      handleEventChange(eventInfo);
    }
  };

  const onDateClick = (arg: { date: Date; allDay: boolean }) => {
    const dateInfo: DateClickInfo = {
      date: arg.date,
      allDay: arg.allDay
    };
    handleDateClick(dateInfo);
  };

  return (
    <div className="p-6 animate-fade-in">
      {/* Enhanced Calendar Header with Gradient Background */}
      <div className="relative rounded-xl bg-gradient-to-br from-card via-muted/30 to-background border border-border/50 backdrop-blur-sm shadow-lg mb-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-accent/5" />
        <div className="relative p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* Navigation Controls with Enhanced Styling */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrev}
                className="hover:bg-primary/10 hover:border-primary/20 transition-all duration-200 hover:shadow-md hover:scale-105"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleToday}
                className="font-medium hover:bg-primary/10 hover:border-primary/20 transition-all duration-200 hover:shadow-md hover:scale-105"
              >
                Today
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleNext}
                className="hover:bg-primary/10 hover:border-primary/20 transition-all duration-200 hover:shadow-md hover:scale-105"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* View Mode Selector with Enhanced Active States */}
            <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 border border-border/30">
              <Button
                variant={viewMode === 'dayGridMonth' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('dayGridMonth')}
                className={`transition-all duration-200 ${
                  viewMode === 'dayGridMonth'
                    ? 'bg-primary text-primary-foreground shadow-md scale-105'
                    : 'hover:bg-background/80 hover:shadow-sm hover:scale-105'
                }`}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Month
              </Button>
              <Button
                variant={viewMode === 'timeGridWeek' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('timeGridWeek')}
                className={`transition-all duration-200 ${
                  viewMode === 'timeGridWeek'
                    ? 'bg-primary text-primary-foreground shadow-md scale-105'
                    : 'hover:bg-background/80 hover:shadow-sm hover:scale-105'
                }`}
              >
                <Grid3X3 className="h-4 w-4 mr-2" />
                Week
              </Button>
              <Button
                variant={viewMode === 'listWeek' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('listWeek')}
                className={`transition-all duration-200 ${
                  viewMode === 'listWeek'
                    ? 'bg-primary text-primary-foreground shadow-md scale-105'
                    : 'hover:bg-background/80 hover:shadow-sm hover:scale-105'
                }`}
              >
                <List className="h-4 w-4 mr-2" />
                List
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced FullCalendar Component */}
      <div className="bg-card rounded-xl border border-border/50 shadow-lg overflow-hidden backdrop-blur-sm">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-transparent to-muted/20 pointer-events-none" />
          <div className="relative">
            <FullCalendar
              ref={calendarRef as React.RefObject<any>}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView={viewMode}
              events={formattedEvents}
              dateClick={onDateClick}
              eventClick={onEventClick}
              eventChange={onEventChange}
              editable={true}
              droppable={true}
              headerToolbar={false}
              height="auto"
              dayMaxEvents={3}
              eventDisplay="block"
              eventTextColor="hsl(var(--foreground))"
              eventBackgroundColor="transparent"
              eventBorderColor="transparent"
              dayHeaderClassNames="text-muted-foreground text-sm font-semibold p-3 bg-muted/30 border-b border-border/30"
              dayCellClassNames="border-border/30 hover:bg-muted/20 transition-colors duration-150"
              eventClassNames="border border-border/40 rounded-lg p-2 mb-1 hover:shadow-md hover:scale-[1.02] transition-all duration-200 cursor-pointer bg-gradient-to-r from-background/80 to-muted/20 backdrop-blur-sm"
              moreLinkClassNames="text-primary hover:text-primary/80 font-medium text-xs hover:underline transition-all duration-150"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
